import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { Deal, FileMetadata } from '@/models/registry';
import { fileMetadataStore } from '@/models/stores/index';
import type { FileMetadata as FileMetadataType } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const useUploadFilesToDealMutation = (dealId: Deal['id']) => {
  const uploadFilesMutation = useMutation({
    onMutate: async (files: FileList) => {
      const fileMetadatas: FileMetadataType[] = Array.from(files).map(
        (file) => ({
          id: uuidv4(),
          deal_id: dealId,
          name: file.name,
          url: 'optimistic-url',
          size: file.size,
          type: file.type,
        }),
      );

      fileMetadatas.forEach((fileMetadata) => {
        fileMetadataStore.add(fileMetadata);
      });
    },
    onSuccess: async (files: FileMetadata[]) => {
      // update the files in the files store
      fileMetadataStore.updateMany(files);
    },
    mutationFn: async (files: FileList): Promise<FileMetadata[]> => {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }

      const response = await pdkAxios.post(`/deals/${dealId}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
  });

  return { uploadFilesMutation };
};

const DealFileDragAndDrop = ({
  children,
  deal,
}: {
  children: React.ReactNode;
  deal: Deal;
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const { uploadFilesMutation } = useUploadFilesToDealMutation(deal.id);

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFilesMutation.mutate(e.dataTransfer.files);
    }
  }, []);
  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn({
        'border-2 border-dashed rounded-md p-6 text-center transition-colors border-blue-500 bg-blue-50':
          isDragging,
      })}
    >
      <div
        className={cn({
          hidden: isDragging,
        })}
      >
        {children}
      </div>
      <div
        className={cn({
          'opacity-0 hidden': !isDragging,
          'opacity-100 flex flex-col items-center justify-center h-screen w-full m-auto':
            isDragging,
        })}
      >
        <h6 className="text-6xl">Drag and drop files for this deal here</h6>

        <p className="text-xl text-gray-500">
          Supported file types: PNG, JPEG, PDF, CSV, TXT
        </p>
      </div>
    </div>
  );
};

export default DealFileDragAndDrop;
